import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

interface Opt { value: string; label: string; }

interface Props {
  value: string | string[];
  options: Opt[];
  onChange: (v: any) => void;
  placeholder?: string;
  title?: string;
  /** Множественный выбор — value/onChange работают с массивом строк */
  multi?: boolean;
  /** Раскрывать список прямо под триггером (внутри контейнера), а не bottom-sheet через портал */
  inline?: boolean;
}

/**
 * Компактный мобильный select: триггер + выезжающий снизу пикер (bottom-sheet)
 * с внутренним скроллом. Заменяет нативный <select>, который на мобилке
 * открывается в непредсказуемом месте и растягивается на весь экран.
 */
const MobileSelect: React.FC<Props> = ({ value, options, onChange, placeholder = '—', title, multi, inline }) => {
  const [open, setOpen] = useState(false);

  const selectedArr = multi ? (Array.isArray(value) ? value : []) : [];
  const isSel = (v: string) => (multi ? selectedArr.includes(v) : value === v);

  const triggerLabel = multi
    ? (selectedArr.length
        ? options.filter((o) => selectedArr.includes(o.value)).map((o) => o.label).join(', ')
        : placeholder)
    : (options.find((o) => o.value === value)?.label ?? placeholder);

  const hasValue = multi ? selectedArr.length > 0 : !!options.find((o) => o.value === value);

  const pick = (v: string) => {
    if (multi) {
      const next = selectedArr.includes(v) ? selectedArr.filter((x) => x !== v) : [...selectedArr, v];
      onChange(next);
    } else {
      onChange(v);
      setOpen(false);
    }
  };

  if (inline) {
    return (
      <div className="m-inline-sel">
        <button type="button" className="m-ts-select" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <span className={`m-ts-select-val${hasValue ? '' : ' ph'}`}>{triggerLabel}</span>
          <FontAwesomeIcon icon={faChevronDown} className={`m-ts-select-chev${open ? ' open' : ''}`} />
        </button>
        {open && (
          <div className="m-inline-list">
            {options.map((o) => (
              <button
                type="button"
                key={o.value}
                className={`m-pick-item${isSel(o.value) ? ' sel' : ''}`}
                onClick={() => pick(o.value)}
              >
                <span>{o.label}</span>
                {isSel(o.value) && <span className="tick">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <button type="button" className="m-ts-select" onClick={() => setOpen(true)}>
        <span className={`m-ts-select-val${hasValue ? '' : ' ph'}`}>{triggerLabel}</span>
        <FontAwesomeIcon icon={faChevronDown} className="m-ts-select-chev" />
      </button>

      {open && createPortal(
        <>
          <div className="m-pick-scrim" onClick={() => setOpen(false)} />
          <div className="m-pick" role="dialog" aria-modal="true">
            <div className="m-sheet-grab" />
            {title && <div className="m-pick-title">{title}</div>}
            <div className="m-pick-list">
              {options.map((o) => (
                <button
                  type="button"
                  key={o.value}
                  className={`m-pick-item${isSel(o.value) ? ' sel' : ''}`}
                  onClick={() => pick(o.value)}
                >
                  <span>{o.label}</span>
                  {isSel(o.value) && <span className="tick">✓</span>}
                </button>
              ))}
            </div>
            {multi && (
              <button type="button" className="btn-primary m-pick-done" onClick={() => setOpen(false)}>
                {`Add${selectedArr.length ? ` · ${selectedArr.length}` : ''}`}
              </button>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

export default MobileSelect;
